import {useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, Stack, Typography} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import StopIcon from '@mui/icons-material/Stop';
import {useTranslation} from 'react-i18next';
import {MediaType} from '../../../../../backend/enums/media-type';
import {StorageFileType} from '../../../../../backend/enums/storage-file-type';
import {translations} from '../../../../../i18n/translations';

type CaptureType = StorageFileType.JPG | StorageFileType.MP4 | StorageFileType.WebMVideo;

export default function MediaCamera({
  onCancel,
  onCapture,
}: {
  onCancel: () => void;
  onCapture: (
    file: File,
    fileType: CaptureType,
    mediaType: MediaType.Photo | MediaType.Video
  ) => void;
}) {
  const {t} = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream>();
  const recorder = useRef<MediaRecorder>();
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const active = useRef(false);
  const starting = useRef(false);
  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    active.current = true;
    let cancelled = false;
    const start = async () => {
      try {
        const result = await navigator.mediaDevices.getUserMedia({
          video: {facingMode: 'environment'},
          audio: false,
        });
        if (cancelled) {
          result.getTracks().forEach((track) => track.stop());
          return;
        }
        stream.current = result;
        if (video.current) video.current.srcObject = result;
        setReady(true);
      } catch {
        if (!cancelled) setError(true);
      }
    };
    if (navigator.mediaDevices) void start();
    else setError(true);
    return () => {
      cancelled = true;
      active.current = false;
      if (timer.current) clearTimeout(timer.current);
      if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
      stream.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  useEffect(() => {
    if (!recording) return undefined;
    const started = Date.now();
    setElapsed(0);
    const interval = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(interval);
  }, [recording]);

  const takePhoto = () => {
    const source = video.current;
    if (!source?.videoWidth || !source.videoHeight) return;
    const canvas = document.createElement('canvas');
    canvas.width = source.videoWidth;
    canvas.height = source.videoHeight;
    canvas.getContext('2d')?.drawImage(source, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (active.current && blob)
          onCapture(
            new File([blob], 'camera.jpg', {type: 'image/jpeg'}),
            StorageFileType.JPG,
            MediaType.Photo
          );
        else if (active.current) setError(true);
      },
      'image/jpeg',
      0.9
    );
  };
  const startRecording = async () => {
    if (!stream.current || recording || starting.current || !window.MediaRecorder) return;
    starting.current = true;
    try {
      const audio = await navigator.mediaDevices.getUserMedia({audio: true});
      if (!active.current || !stream.current) {
        audio.getTracks().forEach((track) => track.stop());
        return;
      }
      audio.getAudioTracks().forEach((track) => stream.current?.addTrack(track));
      const mime = MediaRecorder.isTypeSupported('video/mp4')
        ? 'video/mp4'
        : MediaRecorder.isTypeSupported('video/webm')
          ? 'video/webm'
          : '';
      if (!mime) throw new Error('Recording unavailable');
      const next = new MediaRecorder(stream.current, {mimeType: mime});
      recorder.current = next;
      const chunks: Blob[] = [];
      next.ondataavailable = (event) => {
        if (event.data.size) chunks.push(event.data);
      };
      next.onerror = () => {
        if (active.current) {
          setRecording(false);
          setError(true);
        }
      };
      next.onstop = () => {
        if (timer.current) clearTimeout(timer.current);
        if (!active.current) return;
        setRecording(false);
        const blob = new Blob(chunks, {type: mime});
        if (!blob.size) {
          setError(true);
          return;
        }
        const mp4 = mime === 'video/mp4';
        onCapture(
          new File([blob], mp4 ? 'camera.mp4' : 'camera.webm', {type: mime}),
          mp4 ? StorageFileType.MP4 : StorageFileType.WebMVideo,
          MediaType.Video
        );
      };
      next.start();
      setRecording(true);
      timer.current = setTimeout(() => {
        if (next.state === 'recording') next.stop();
      }, 300000);
    } catch {
      if (active.current) setError(true);
    } finally {
      starting.current = false;
    }
  };
  return (
    <Stack spacing={1}>
      {error && <Alert severity="error">{t(translations.family_media.camera_error)}</Alert>}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'black',
          borderRadius: 1,
          overflow: 'hidden',
          width: '100%',
          aspectRatio: '16 / 9',
          maxHeight: 320,
        }}
      >
        <Box
          component="video"
          ref={video}
          autoPlay
          muted
          playsInline
          sx={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            transform: 'scaleX(-1)',
          }}
        />
        {recording && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            role="status"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              bgcolor: 'rgba(0,0,0,0.75)',
              color: 'white',
              borderRadius: 1,
              px: 1.5,
              py: 0.75,
            }}
          >
            <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.main'}} />
            <Typography variant="body2" fontWeight={600}>
              {t(translations.family_media.recording)}{' '}
              {String(Math.floor(elapsed / 60)).padStart(2, '0')}:
              {String(elapsed % 60).padStart(2, '0')} / 05:00
            </Typography>
          </Stack>
        )}
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
        <Button startIcon={<ArrowBackIcon />} onClick={onCancel}>
          {t(translations.family_media.cancel)}
        </Button>
        <Button
          variant="contained"
          startIcon={<PhotoCameraIcon />}
          disabled={!ready || recording}
          onClick={takePhoto}
        >
          {t(translations.family_media.take_photo)}
        </Button>
        {recording ? (
          <Button
            variant="contained"
            color="error"
            startIcon={<StopIcon />}
            onClick={() => recorder.current?.stop()}
          >
            {t(translations.family_media.stop_recording)}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            startIcon={<VideocamIcon />}
            disabled={!ready || !window.MediaRecorder}
            onClick={() => void startRecording()}
          >
            {t(translations.family_media.record_video)}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
